import React, { type ReactNode } from "react";
import { Flex, Text, SimpleGrid, Box } from "@chakra-ui/react";
import { SolidPlate } from "ui/plates";
import { TextTitle } from "ui/text";

type ValuePairProps = {
  step: string;
  title: string;
  description: ReactNode;
};

function ValuePair({ title, description, step }: ValuePairProps) {
  return (
    <Box maxWidth="500px">
      <Flex align="end" borderRadius="10px" boxSize="48px" bg="new.blue.300" color="new.blue.950" pl="tinier">
        <Text fontSize={{ base: "brand.lg", md: "brand.xl" }} fontWeight="bold">
          {step}
        </Text>
      </Flex>

      <TextTitle title={title} titleAs="h3" description={description} theme="dark" size="sm" mt="small" />
    </Box>
  );
}

export function Values() {
  return (
    <SolidPlate bgColor="new.blue.950" theme="dark" spotlightColor="new.blue.900">
      <Text as="h2" textStyle={{ base: "headline1", md: "display2" }} align="center">
        Our values
      </Text>

      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        justifyContent="space-between"
        gap={20}
        mt={{ base: "biggest", md: "huge" }}
      >
        <ValuePair
          step="01"
          title="Forever hungry"
          description={
            <>
              <Text as="span" display="flex">
                We are hungry and ambitious. We celebrate our accomplishments, but we’re never fully satisfied. We’re
                always figuring out how to collectively push ourselves further and do more.
              </Text>

              <Text as="span" display="flex" mt="smaller">
                If we think we can grow the company 5x this year, the first question should be “why not 10x?”
              </Text>
            </>
          }
        />

        <ValuePair
          step="02"
          title="Kindness"
          description={
            <>
              <Text as="span" display="flex">
                We want to create an environment where people feel actively welcomed, encouraged, and supported. People
                who aren’t kind aren’t tolerated— it’s just not worth it.
              </Text>

              <Text as="span" display="flex" mt="smaller">
                We intrinsically believe in a deeper kindness as a core value, aside from its obvious benefits to the
                business
              </Text>
            </>
          }
        />

        <ValuePair
          step="03"
          title="Efficient execution"
          description={
            <>
              <Text as="span" display="flex">
                Speed matters. We don’t have time for endless deliberation– most decisions are two-way doors. Move fast,
                adapt quickly.
              </Text>

              <Text as="span" display="flex" mt="smaller">
                We take inspiration from others and don’t innovate where we don’t need to. We communicate clearly
                because time is precious. We parallelize to the greatest extent possible.
              </Text>
            </>
          }
        />

        <ValuePair
          step="04"
          title="Compassion"
          description={
            <>
              <Text as="span" display="flex">
                We listen to everyone and try to put ourselves in their shoes, regardless of our initial reaction to
                what they say.
              </Text>
              <Text as="span" display="flex" mt="smaller">
                This applies to everyone– customers, prospects, partners, peers, etc.
              </Text>
            </>
          }
        />

        <ValuePair
          step="05"
          title="Impact driven"
          description={
            <>
              <Text as="span" display="flex">
                Everyone should be intrinsically motivated by business impact. We minimize distractions and prioritize
                our time based on what’s actually impactful to the business.
              </Text>

              <Text as="span" display="flex" mt="smaller">
                We value people at all levels based on their impact above anything else.
              </Text>
            </>
          }
        />

        <ValuePair
          step="06"
          title="Raising the bar"
          description={
            <>
              <Text as="span" display="flex">
                We have high expectations for performance and believe in having exceptional talent in every position. We
                understand the value of great people.
              </Text>

              <Text as="span" display="flex" mt="smaller">
                We look deeper than credentials, prioritize slope over y-intercept, and put in the hard work to find
                those that truly raise the bar.
              </Text>
            </>
          }
        />

        <ValuePair
          step="07"
          title="Humility"
          description={
            <>
              <Text as="span" display="flex">
                We are humble. Listening is mission critical— we are open to others’ perspectives and ideas. No work is
                beneath us. We also believe that humility leads to foresight.
              </Text>

              <Text as="span" display="flex" mt="smaller">
                If we are not grounded and open minded, we blind ourselves from key opportunities and risks in every
                aspect of business.
              </Text>
            </>
          }
        />
      </SimpleGrid>
    </SolidPlate>
  );
}
